import {
  createInstance,
  ModuleFederation
} from '@module-federation/enhanced/runtime';

// Import single functions, not the 'semver' index, to keep the client bundle small
import valid from 'semver/functions/valid';

import { useMfeUrl } from '../use-mfe-url';
import {
  isLegacySchedulingUiVersion,
  useIsLegacySchedulingUi
} from './legacy-version';

import * as React from 'react';
import * as ReactDOM from 'react-dom';
import * as ReactJsxRuntime from 'react/jsx-runtime';
import * as ReactJsxDevRuntime from 'react/jsx-dev-runtime';
import * as ReactDOMServer from 'react-dom/server';
import * as axios from 'axios';
import * as EmotionReact from '@emotion/react';
import * as muiMaterial from '@mui/material';

// Define shared dependency structure
interface SharedDependency {
  singleton: boolean;
  lib: () => any;
  version: string;
}

interface SharedDependencies {
  [key: string]: SharedDependency;
}

// Define available modules in the scheduling UI
export type SchedulingUiModules =
  | 'KyruusSchedulingUI'
  | 'KyruusManagementUI'
  | 'KyruusCancellationUI'
  | 'KyruusWaitlistUI'
  | 'KyruusAggregatedAvailabilityUI';

/**
 * Global declaration for webpack injected dependency versions
 */
declare const __DEPENDENCY_VERSIONS__: {
  [key: string]: string;
};

// instantiate the getters outside of the function to keep
// references to the original libs stable. helps with unit testing
const libGetters = {
  react: () => React,
  'react-dom': () => ReactDOM,
  'react/jsx-runtime': () => ReactJsxRuntime,
  'react/jsx-dev-runtime': () => ReactJsxDevRuntime,
  'react-dom/server': () => ReactDOMServer,
  '@emotion/react': () => EmotionReact,
  axios: () => axios,
  '@mui/material': () => muiMaterial
};

/**
 * These are the dependencies shared between this providermatch-consumer application and
 * the imported scheduling MFE.
 * __DEPENDENCY_VERSIONS__ are injected via a custom build plugin
 *
 * scheduling-ui >= 2.6.0 expects the JSX runtimes and react-dom/server (used by
 * @kyruus/rich-text) from the host; without them it falls back to its bundled
 * React 17 copies, which crash on this React 19 host.
 */
export function getSchedulingUiSharedDependencies(): SharedDependencies {
  return {
    react: {
      singleton: true,
      lib: libGetters['react'],
      version: __DEPENDENCY_VERSIONS__['react']
    },
    'react/jsx-runtime': {
      singleton: true,
      lib: libGetters['react/jsx-runtime'],
      version: __DEPENDENCY_VERSIONS__['react']
    },
    'react/jsx-dev-runtime': {
      singleton: true,
      lib: libGetters['react/jsx-dev-runtime'],
      version: __DEPENDENCY_VERSIONS__['react']
    },
    'react-dom/server': {
      singleton: true,
      lib: libGetters['react-dom/server'],
      version: __DEPENDENCY_VERSIONS__['react-dom']
    },
    '@emotion/react': {
      singleton: true,
      lib: libGetters['@emotion/react'],
      version: __DEPENDENCY_VERSIONS__['@emotion/react']
    },
    'react-dom': {
      singleton: true,
      lib: libGetters['react-dom'],
      version: __DEPENDENCY_VERSIONS__['react-dom']
    },
    axios: {
      singleton: true,
      lib: libGetters['axios'],
      version: __DEPENDENCY_VERSIONS__['axios']
    },
    '@mui/material': {
      singleton: true,
      lib: libGetters['@mui/material'],
      version: __DEPENDENCY_VERSIONS__['@mui/material']
    }
  };
}

/**
 * Load a module federation instance of the scheduling UI micro frontend
 * @param url The URL to the micro frontend manifest
 * @returns A configured module federation instance
 */
export function loadSchedulingUiMfe(url: string): ModuleFederation {
  if (!url) {
    throw new Error('scheduling ui url is missing');
  }
  const schedulingMf = createInstance({
    name: 'scheduling',
    shareStrategy: 'loaded-first',
    remotes: [
      {
        name: 'scheduling',
        entry: url, // 'https://lcl.kyruus.com:4000/current/mf-manifest.json',
        alias: 'scheduling'
      }
    ]
  });

  schedulingMf.registerShared(getSchedulingUiSharedDependencies());

  return schedulingMf;
}

/**
 * Build versions read from each manifest URL, so the five scheduling-ui call
 * sites on a page share one request per URL.
 */
const manifestBuildVersions = new Map<string, Promise<string | null>>();

/**
 * Read `metaData.buildInfo.buildVersion` from a module federation manifest.
 * Resolves to null (never rejects) if the manifest can't be fetched or parsed
 * or has no string build version; a failed read isn't cached, so a later
 * mount tries again.
 * @param url The resolved `mf-manifest.json` URL (the federation `entry`)
 */
export function fetchSchedulingUiBuildVersion(
  url: string
): Promise<string | null> {
  const cached = manifestBuildVersions.get(url);
  if (cached) {
    return cached;
  }
  const request = fetch(url)
    .then((response) => (response.ok ? response.json() : null))
    .then((manifest) => {
      const buildVersion = manifest?.metaData?.buildInfo?.buildVersion;
      return typeof buildVersion === 'string' ? buildVersion : null;
    })
    .catch(() => null)
    .then((buildVersion) => {
      if (buildVersion === null) {
        manifestBuildVersions.delete(url);
      }
      return buildVersion;
    });
  manifestBuildVersions.set(url, request);
  return request;
}

/**
 * Decide whether the scheduling-ui build served at `url` needs the React 17
 * island. The served build's own version (`buildVersion` in its manifest) wins,
 * so a `current` pin that points at a pre-2.6.0 build (release-order slip or a
 * rollback) still gets the island. If the manifest can't be read or has no
 * valid semver build version, the pin-string rule (`pinIsLegacy`) decides.
 * @param url The resolved manifest URL
 * @param pinIsLegacy The pin-string decision from `useIsLegacySchedulingUi`
 */
export async function resolveIsLegacySchedulingUi(
  url: string,
  pinIsLegacy: boolean
): Promise<boolean> {
  const buildVersion = await fetchSchedulingUiBuildVersion(url);
  if (!buildVersion || !valid(buildVersion)) {
    return pinIsLegacy;
  }
  return isLegacySchedulingUiVersion(buildVersion);
}

/**
 * Test-only: clear the manifest cache between tests.
 * @internal
 */
export function __resetSchedulingUiRoutingForTest__() {
  manifestBuildVersions.clear();
}

function loadNormalSchedulingModule(
  mfeUrl: string,
  moduleName: SchedulingUiModules
): Promise<any> {
  const mf = loadSchedulingUiMfe(mfeUrl);
  switch (moduleName) {
    case 'KyruusCancellationUI':
      return mf.loadRemote('scheduling/KyruusCancellationUI');
    case 'KyruusManagementUI':
      return mf.loadRemote('scheduling/KyruusManagementUI');
    case 'KyruusSchedulingUI':
      return mf.loadRemote('scheduling/KyruusSchedulingUI');
    case 'KyruusWaitlistUI':
      return mf.loadRemote('scheduling/KyruusWaitlistUI');
    case 'KyruusAggregatedAvailabilityUI':
      return mf.loadRemote('scheduling/KyruusAggregatedAvailabilityUI');
  }
  return Promise.reject(
    new Error(`scheduling-ui: unknown module ${moduleName as string}`)
  );
}

/**
 * Interface for the hook parameters
 */
export interface SchedulingModuleOptions {
  /** URL to the micro frontend manifest */
  url?: string;
  /** Name of the module to load from the scheduling UI */
  moduleName: SchedulingUiModules;
}

/**
 * Hook for importing a scheduling module from the scheduling MFE. The modules are exported from
 * the scheduling-ui project.
 *
 * Builds before scheduling-ui 2.6.0 (built on React 17) are loaded through a React 17 island to
 * avoid React version conflicts. The lazy loader reads the served build's version from its
 * manifest and falls back to the pinned version (see `resolveIsLegacySchedulingUi`). Components
 * rendered through the island must use plain props only (no React 19 elements, refs, or context
 * providers in props).
 *
 * Returns null until both the manifest URL and the pin-based fallback are known. Both come from
 * the version manager, which is set in the same effect pass, so in practice the URL arrives last;
 * the pin check only guards against routing on a missing fallback.
 *
 * @param options The options for loading the module
 * @returns A lazy-loaded React component
 */
export function useSchedulingModule({
  moduleName
}: SchedulingModuleOptions): React.LazyExoticComponent<
  React.ComponentType<any>
> | null {
  const mfeUrl = useMfeUrl('scheduling-ui');
  const pinIsLegacy = useIsLegacySchedulingUi();

  return React.useMemo(() => {
    if (!mfeUrl || pinIsLegacy === null) {
      return null;
    }

    return React.lazy(async (): Promise<any> => {
      if (await resolveIsLegacySchedulingUi(mfeUrl, pinIsLegacy)) {
        // pre-2.6.0 build: the React 17 island, in its own lazy chunk
        const island = await import(
          /* webpackChunkName: "scheduling-ui-island" */ './legacy-island'
        );
        return island.loadIslandComponent(mfeUrl, moduleName);
      }
      return loadNormalSchedulingModule(mfeUrl, moduleName);
    });
  }, [mfeUrl, moduleName, pinIsLegacy]);
}

/**
 * Whether the scheduling-ui build for this page runs in the React 17 island,
 * using the same decision as `useSchedulingModule`. Null until it is known.
 * Hosts of island content (the direct-book drawer) use it to adapt to the
 * island's separate MUI copy.
 */
export function useIsSchedulingUiIsland(): boolean | null {
  const mfeUrl = useMfeUrl('scheduling-ui');
  const pinIsLegacy = useIsLegacySchedulingUi();
  const [resolved, setResolved] = React.useState<{
    key: string;
    isIsland: boolean;
  } | null>(null);

  const key =
    mfeUrl && pinIsLegacy !== null ? `${pinIsLegacy}|${mfeUrl}` : null;

  React.useEffect(() => {
    if (!mfeUrl || pinIsLegacy === null) {
      return undefined;
    }
    let active = true;
    resolveIsLegacySchedulingUi(mfeUrl, pinIsLegacy).then((isIsland) => {
      if (active) {
        setResolved({ key: `${pinIsLegacy}|${mfeUrl}`, isIsland });
      }
    });
    return () => {
      active = false;
    };
  }, [mfeUrl, pinIsLegacy]);

  return resolved && resolved.key === key ? resolved.isIsland : null;
}
